import { PhotoAvatar, Skeleton, Text } from "@growtherapy/sprout-ui";
import { Provider } from "../../types";
import { useGetTelehealthSessionInfo } from "../../hooks";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export type ProviderAvatarSectionProps = {
  provider?: Pick<Provider, "image" | "name" | "pronouns">;
  loading?: boolean;
  className?: string;
};

export function ProviderAvatarSection({
  provider,
  loading,
  className,
}: ProviderAvatarSectionProps) {
  return (
    <div
      data-testid="provider-avatar-section"
      className={twMerge("flex flex-row gap-2 items-start w-full", className)}
    >
      {loading ? (
        <>
          <span data-testid="provider-avatar-section.image-skeleton">
            <Skeleton className="my-0 mx-0 rounded-full w-24 h-24" />
          </span>
          <span data-testid="provider-avatar-section.name-skeleton">
            <Skeleton className="my-0 mx-0 w-28" />
          </span>
        </>
      ) : (
        <div
          data-dd-privacy="mask"
          className="fs-exclude flex flex-row gap-4  items-start content-center"
          data-testid="provider-avatar-section.image-and-name"
        >
          <PhotoAvatar
            alt={`Provider ${provider?.name ?? "picture"}`}
            src={provider?.image ?? ""}
            size="md"
          />
          <div className="flex flex-col h-full justify-center my-auto ">
            <Text as="h1" variant="xl" className="font-medium">
              Let&rsquo;s get ready
            </Text>
            <Text
              data-testid="provider-avatar-section.provider-name"
              variant="lg"
              className="text-neutral-700 font-medium text-md leading-8"
              data-dd-action-name="Click on provider avatar"
            >
              Session with {provider?.name ?? "provider"}
              {(provider?.pronouns?.length ?? 0) > 0 && (
                <span
                  className="font-normal"
                  data-testid="provider-avatar-section.provider-pronouns"
                >
                  {" "}
                  ({provider?.pronouns![0]})
                </span>
              )}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}

export function ProviderAvatarSectionWrapper(
  props: Pick<ProviderAvatarSectionProps, "className">,
) {
  const { providerShortId, patientShortId } = useParams();
  const { data, loading } = useGetTelehealthSessionInfo(
    providerShortId,
    patientShortId,
  );
  const provider = data?.telehealthSessionInfo?.provider;
  return (
    <ProviderAvatarSection provider={provider} loading={loading} {...props} />
  );
}
